import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useId,
  useInteractions,
  useRole,
  useDismiss
} from '@floating-ui/react';
import { PropsWithChildren, useState } from 'react';

import { Color } from '@/constants/color';
import Badge from '@/ui/components/Badge';

import { parseAbout, PILL_SECTIONS } from '../StaffProfileCard/parseAbout';

import { Props } from './types';

const StaffProfileHoverCard = ({
  profile,
  placement = 'bottom-start',
  children
}: PropsWithChildren<Props>) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [offset(8), flip({ fallbackAxisSideDirection: 'start' }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate
  });

  const hover = useHover(context, { move: false, handleClose: safePolygon() });
  const role = useRole(context, { role: 'dialog' });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, role, dismiss]);
  const id = useId();

  const { staff, photo_url } = profile;
  const displayName = staff.display_name || `${staff.first_name} ${staff.last_name}`;
  const uniqueCredentials = [...new Set(staff.credentials)].filter(Boolean);
  const sections = parseAbout(profile.about);

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps({ 'aria-describedby': isOpen ? id : undefined })}>
        {children}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            id={id}
            {...getFloatingProps()}
            className="z-50 w-72 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 overflow-hidden">
            <div className="p-3 space-y-3">
              {/* Header */}
              <div className="flex items-center gap-2.5">
                {photo_url ? (
                  <img
                    src={photo_url}
                    alt={displayName}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-gray-200"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-gray-600 font-semibold text-sm">
                      {staff.first_name?.[0]}
                      {staff.last_name?.[0]}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm leading-snug">
                    {displayName}
                    {uniqueCredentials.length > 0 && (
                      <span className="font-normal text-gray-500">
                        {', '}
                        {uniqueCredentials.join(', ')}
                      </span>
                    )}
                  </p>
                  {staff.primary_role && (
                    <p className="text-xs text-gray-500 italic">{staff.primary_role.name}</p>
                  )}
                </div>
              </div>

              {/* About sections */}
              {sections.length > 0 && (
                <div className="space-y-3 border-t border-gray-100 pt-2">
                  {sections.map((section, idx) => (
                    <div key={section.title || idx}>
                      {section.title && (
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-800 mb-1.5">
                          {section.title}
                        </p>
                      )}
                      {PILL_SECTIONS.has(section.title) ? (
                        <div className="flex flex-wrap gap-1">
                          {section.content
                            .split(',')
                            .map((item) => item.trim())
                            .filter(Boolean)
                            .map((item) => (
                              <Badge key={item} color={Color.SLATE} text={item} />
                            ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-700 line-clamp-3">{section.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default StaffProfileHoverCard;
